import { Card } from "@/components/ui/card";
import { API } from "@/lib/api";
import EvidenceBadge from "@/components/EvidenceBadge";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
  LineChart, Line, Legend, PieChart, Pie, Cell,
} from "recharts";
import {
  Building2, MapPin, ShieldCheck, AlertTriangle, CheckCircle2, HelpCircle,
  ArrowRight, ExternalLink, TrendingUp, Info,
} from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString()}`;
const pct = (n) => `${Number(n || 0).toFixed(1)}%`;

const ROADMAP_LABEL = {
  validate: "Validate", partner: "Partner", underwrite: "Final Underwriting",
  contract: "Contract / Acquire", prepare: "Prepare Property", compliance: "Compliance / Inspection",
  activate: "Activate Referral", place: "Place Residents", stabilize: "Stabilize",
  operate: "Operate",
};

/**
 * OpportunityReport — read-only, investor-grade presentation of a packet.
 * data: shape returned by GET /api/packets/:id/report or /api/public/packets/:token/report
 * mode: "external" | "internal-preview"
 */
export default function OpportunityReport({ data, mode = "external" }) {
  if (!data) return null;
  const {
    property: prop = {}, program: prog, agency, underwriting: uw,
    sensitivity: sens, score, thesis, sections = {},
    verified_items = [], assumed_items = [], unresolved_items = [],
    risks = [], exit_options = [], sources = [],
    roadmap_stages = [], roadmap_current, stage,
    performance = {}, share_expires_at, title, compliance, created_at,
    packet_type = "conceptual", verification = {},
  } = data;
  const isConceptual = (packet_type || "conceptual") === "conceptual";

  const token = typeof window !== "undefined" ? (localStorage.getItem("sahoi_token") || "") : "";
  const photoUrl = (ph) => `${API}/files/${ph.storage_path}${mode === "external" ? "" : `?auth=${encodeURIComponent(token)}`}`;
  const heroPhoto = (prop.photos || [])[0];

  const specNoi = uw?.scenarios?.specialized?.noi || 0;
  const convNoi = uw?.scenarios?.conventional?.noi || 0;
  const specCf = uw?.scenarios?.specialized?.annual_cash_flow || 0;
  const convCf = uw?.scenarios?.conventional?.annual_cash_flow || 0;
  const specRev = uw?.scenarios?.specialized?.effective_gross_income || 0;
  const convRev = uw?.scenarios?.conventional?.effective_gross_income || 0;
  const specOpex = uw?.scenarios?.specialized?.operating_expenses || 0;

  const isOperational = stage === "operational";

  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${prop.address}, ${prop.city}, TX ${prop.zip_code || ""}`.trim())}`;

  return (
    <div className="min-h-screen bg-[#F8FAF9] text-slate-900" data-testid="opportunity-report">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .sticky { position: static !important; }
          [data-testid="opportunity-report"] section,
          [data-testid="opportunity-report"] table,
          [data-testid="opportunity-report"] .report-avoid-break { break-inside: avoid; page-break-inside: avoid; }
          [data-testid="conceptual-banner"] { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
      {/* Conceptual banner — visible on screen and in print */}
      {isConceptual && (
        <div className="bg-amber-500 text-white" data-testid="conceptual-banner">
          <div className="max-w-5xl mx-auto px-6 py-2 text-xs font-semibold uppercase tracking-wide flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" /> Conceptual — facts not verified, not for investment reliance
          </div>
        </div>
      )}
      {/* Header */}
      <div className="border-b border-slate-200 bg-white sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-md bg-slate-900 text-white flex items-center justify-center font-bold text-sm shrink-0">SA</div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Throughline · Property &amp; Housing Opportunity Report</div>
              <div className="text-sm font-semibold truncate">{title}</div>
            </div>
          </div>
          {isOperational && (
            <span className="text-[10px] px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 font-semibold uppercase tracking-wider" data-testid="stage-badge">Operational</span>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 lg:py-12 space-y-10">
        {/* Executive Snapshot */}
        <section data-testid="report-snapshot">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Executive opportunity snapshot</div>
          <h1 className="text-3xl lg:text-4xl font-bold mt-1 leading-tight">{title}</h1>
          <div className="text-sm text-slate-500 mt-1 flex items-center gap-1 flex-wrap">
            <Building2 className="w-3.5 h-3.5" /> {prop.address}, {prop.city} · {prop.county} County · TX {prop.zip_code}
            <a href={mapsUrl} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-1 text-[#C25E00] hover:underline">
              <MapPin className="w-3 h-3" /> Open in Maps
            </a>
          </div>

          <div className="grid md:grid-cols-5 gap-4 mt-6">
            <Card className="md:col-span-3 p-6 border-slate-200 bg-white">
              {heroPhoto && (
                <img src={photoUrl(heroPhoto)} alt={prop.address} className="w-full h-56 object-cover rounded-md mb-4" />
              )}
              <h3 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Opportunity thesis</h3>
              <p className="text-sm text-slate-800 leading-relaxed mt-1 whitespace-pre-wrap">{thesis || sections.executive_summary || "Thesis pending."}</p>
              <div className="mt-5">
                <h3 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Key unresolved items</h3>
                <ul className="mt-2 space-y-1 text-sm">
                  {(unresolved_items || []).slice(0, 5).map((u, i) => (
                    <li key={i} className="flex items-start gap-2"><HelpCircle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" /><span>{u}</span></li>
                  ))}
                  {(unresolved_items || []).length === 0 && <li className="text-slate-500 text-xs">No open items flagged.</li>}
                </ul>
              </div>
            </Card>
            <Card className="md:col-span-2 p-6 border-slate-200 bg-white space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <ScoreTile label="Opportunity Score" value={score?.opportunity_score} accent />
                <ScoreTile label="Confidence Score" value={score?.confidence_score} />
              </div>
              <Kv k="Projected specialized revenue" v={money(specRev)} accent />
              <Kv k="Conventional rental benchmark" v={money(convRev)} />
              <Kv k="Projected NOI (specialized)" v={uw ? money(specNoi) : "Not yet modeled"} />
              <Kv k="Projected cash flow (specialized)" v={uw ? `${money(specCf)}/yr` : "Not yet modeled"} />
              <Kv k="Acquisition assumption" v={`${money(prop.acquisition_cost)} + ${money(prop.conversion_cost)} conv.`} />
            </Card>
          </div>
        </section>

        {/* Property Overview */}
        <section data-testid="report-property">
          <H2>Property overview</H2>
          <div className="grid md:grid-cols-3 gap-4">
            <Card className="md:col-span-2 p-5 border-slate-200 bg-white">
              {(prop.photos || []).length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
                  {(prop.photos || []).slice(0, 6).map(ph => (
                    <img key={ph.id} src={photoUrl(ph)} alt={ph.filename} className="aspect-video w-full object-cover rounded-md" />
                  ))}
                </div>
              ) : (
                <div className="text-xs text-slate-500 py-6 text-center border border-dashed border-slate-200 rounded-md mb-4">No photos on file.</div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <Info2 k="Type" v={(prop.property_type || "").replace("_", " ")} />
                <Info2 k="Beds / Baths" v={`${prop.bedrooms || 0} / ${prop.bathrooms || 0}`} />
                <Info2 k="Sqft" v={prop.sqft || "—"} />
                <Info2 k="Year built" v={prop.year_built || "—"} />
                <Info2 k="Zoning" v={prop.zoning || "—"} />
                <Info2 k="Accessibility" v={prop.accessibility || "—"} />
                <Info2 k="Parking" v={prop.parking || "—"} />
                <Info2 k="Lot" v={prop.lot_size || "—"} />
              </div>
              {prop.notes && <div className="mt-4 pt-3 border-t border-slate-100 text-xs text-slate-600 leading-relaxed">{prop.notes}</div>}
            </Card>
            <Card className="p-5 border-slate-200 bg-white space-y-3">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Acquisition assumptions</div>
              <Kv k="Acquisition cost" v={money(prop.acquisition_cost)} />
              <Kv k="Conversion / capex" v={money(prop.conversion_cost)} />
              <Kv k="Total capital" v={money((prop.acquisition_cost || 0) + (prop.conversion_cost || 0))} accent />
              <div className="pt-3 border-t border-slate-100 text-[11px] text-slate-500">
                Amounts represent assumptions captured on the property record; not offers or firm quotes.
              </div>
            </Card>
          </div>
        </section>

        {/* Critical facts & evidence */}
        <section data-testid="report-critical-facts">
          <H2>Critical facts &amp; evidence</H2>
          <Card className="p-5 border-slate-200 bg-white overflow-x-auto report-avoid-break">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  <th className="py-2 pr-4 font-semibold text-slate-600 text-xs uppercase tracking-wider">Fact</th>
                  <th className="py-2 pr-4 font-semibold text-slate-600 text-xs uppercase tracking-wider">Value</th>
                  <th className="py-2 pr-4 font-semibold text-slate-600 text-xs uppercase tracking-wider">Evidence level</th>
                  <th className="py-2 font-semibold text-slate-600 text-xs uppercase tracking-wider">Source</th>
                </tr>
              </thead>
              <tbody>
                {(verification?.fields || []).map((f) => {
                  const raw = f.value;
                  let val = "—";
                  if (raw != null && raw !== "" && raw !== 0) {
                    val = f.field === "acquisition_price" && !isNaN(Number(raw)) ? money(raw) : String(raw);
                  }
                  return (
                    <tr key={f.field} className="border-b border-slate-100 last:border-b-0" data-testid={`critical-fact-${f.field}`}>
                      <td className="py-2 pr-4 text-slate-700 capitalize">{(f.field || "").replace(/_/g, " ")}</td>
                      <td className="py-2 pr-4 font-mono text-slate-900">{val}</td>
                      <td className="py-2 pr-4"><EvidenceBadge level={f.evidence_level || "needs_verification"} /></td>
                      <td className="py-2 text-slate-600">{f.source || "—"}</td>
                    </tr>
                  );
                })}
                {(verification?.fields || []).length === 0 && (
                  <tr><td colSpan={4} className="py-3 text-xs italic text-slate-500">No critical facts recorded on this property yet.</td></tr>
                )}
              </tbody>
            </table>
            <div className="text-[11px] text-slate-500 mt-3 italic">Financial projections in this report are estimates derived from the inputs and facts above.</div>
          </Card>
        </section>

        {/* Housing Need & Proposed Use */}
        {prog && (
          <section data-testid="report-need">
            <H2>Housing need &amp; proposed use</H2>
            <Card className="p-6 border-slate-200 bg-white">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Population served</div>
                  <div className="text-sm text-slate-800 mt-1">{prog.population || "—"}</div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mt-4">Housing problem addressed</div>
                  <div className="text-sm text-slate-800 mt-1">{sections.target_population || prog.demand_notes || "The program addresses households facing housing instability within the specified population."}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Proposed housing model</div>
                  <div className="text-sm text-slate-800 mt-1 capitalize">{(prog.vertical || "").replace("_", " ")}</div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mt-4">Why this property may fit</div>
                  <div className="text-sm text-slate-800 mt-1 whitespace-pre-wrap">{sections.housing_model || (score?.reasons || []).join(" • ") || "See scoring reasons below."}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6 pt-6 border-t border-slate-100">
                <Kv k="Geographic demand" v={(prog.geography || []).slice(0, 4).join(", ") || "—"} />
                <Kv k="Waitlist / capacity" v={prog.waitlist_size || prog.capacity || "—"} />
                <Kv k="Avg length of stay" v={prog.avg_length_of_stay || "—"} />
                <Kv k="Referral velocity" v={referralVelocity(prog.waitlist_size)} />
              </div>
            </Card>
          </section>
        )}

        {/* Payment Flow */}
        {prog && (
          <section data-testid="report-flow">
            <H2>Program, demand &amp; payment flow</H2>
            <Card className="p-6 border-slate-200 bg-white">
              <FlowDiagram
                funder={prog.funder || "—"}
                program={prog.name}
                agency={prog.agency || prog.admin_agent || "—"}
                operator={prog.admin_agent || prog.agency || "—"}
                resident={prog.population || "—"}
                propertyAddr={`${prop.address}`}
                payment={prog.payment_flow || "—"}
                verified={score?.confidence_score >= 70}
              />
              <div className="grid md:grid-cols-3 gap-3 mt-6 pt-6 border-t border-slate-100 text-sm">
                <Kv k="Payment basis" v={(prog.rent_basis || "per_unit").replace("_", " ").toUpperCase()} />
                <Kv k="Payment amount" v={prog.payment_amount || "Not yet documented"} accent={!!prog.payment_amount} />
                <Kv k="Contract type" v={prog.contract_type || "—"} />
              </div>
              {agency && agency.website && (
                <div className="mt-4 text-xs text-slate-500">
                  Referring agency: <a href={agency.website} target="_blank" rel="noreferrer" className="text-[#C25E00] hover:underline inline-flex items-center gap-1">{agency.name} <ExternalLink className="w-3 h-3" /></a>
                </div>
              )}
            </Card>
          </section>
        )}

        {/* Evidence Dashboard */}
        <section data-testid="report-evidence">
          <H2>Evidence &amp; validation</H2>
          <div className="grid md:grid-cols-3 gap-4">
            <EvidenceCol title="Verified facts" icon={CheckCircle2} tone="emerald" items={verified_items} empty="None captured yet." />
            <EvidenceCol title="Assumptions / estimates" icon={Info} tone="amber" items={assumed_items} empty="None captured yet." />
            <EvidenceCol title="Needs verification" icon={HelpCircle} tone="rose" items={unresolved_items} empty="No open items." />
          </div>
        </section>

        {/* Financial Analysis */}
        <section data-testid="report-financials">
          <H2>Financial analysis</H2>
          {!uw ? (
            <Card className="p-6 border-slate-200 bg-white">
              <div className="text-sm text-slate-500 italic">Not yet modeled — no underwriting inputs on this property.</div>
            </Card>
          ) : (
            <div className="grid md:grid-cols-3 gap-4">
              <Card className="md:col-span-2 p-5 border-slate-200 bg-white">
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Specialized vs Conventional — NOI &amp; Cash flow</div>
                <div className="w-full h-72" style={{ minWidth: 0 }}>
                  <ResponsiveContainer width="100%" height="100%" minHeight={280}>
                    <BarChart data={[
                      { name: "NOI", Specialized: specNoi, Conventional: convNoi },
                      { name: "Cash flow", Specialized: specCf, Conventional: convCf },
                    ]} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                      <Tooltip formatter={(v) => money(v)} />
                      <Legend />
                      <Bar dataKey="Specialized" fill="#C25E00" radius={[4,4,0,0]} />
                      <Bar dataKey="Conventional" fill="#0F172A" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
              <Card className="p-5 border-slate-200 bg-white">
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Revenue vs Operating expense (specialized)</div>
                <div className="w-full h-72" style={{ minWidth: 0 }}>
                  <ResponsiveContainer width="100%" height="100%" minHeight={280}>
                    <PieChart>
                      <Pie data={[
                        { name: "Operating expense", value: specOpex },
                        { name: "NOI", value: Math.max(0, specNoi) },
                      ]} innerRadius={50} outerRadius={80} dataKey="value" label={({ percent }) => `${(percent * 100).toFixed(0)}%`}>
                        <Cell fill="#94A3B8" /><Cell fill="#C25E00" />
                      </Pie>
                      <Tooltip formatter={(v) => money(v)} />
                      <Legend verticalAlign="bottom" height={30} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              <Card className="md:col-span-3 p-5 border-slate-200 bg-white">
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Financial statement (annual)</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 font-semibold text-slate-600 text-xs uppercase tracking-wider">Line item</th>
                        <th className="text-right py-2 font-semibold text-[#C25E00] text-xs uppercase tracking-wider">Specialized</th>
                        <th className="text-right py-2 font-semibold text-slate-800 text-xs uppercase tracking-wider">Conventional</th>
                        <th className="text-right py-2 font-semibold text-slate-500 text-xs uppercase tracking-wider">Δ</th>
                      </tr>
                    </thead>
                    <tbody className="font-mono text-sm">
                      <FinRow label="Gross monthly rent" a={uw.scenarios.specialized.gross_monthly_rent} b={uw.scenarios.conventional.gross_monthly_rent} />
                      <FinRow label="Effective gross income" a={uw.scenarios.specialized.effective_gross_income} b={uw.scenarios.conventional.effective_gross_income} />
                      <FinRow label="Operating expenses" a={uw.scenarios.specialized.operating_expenses} b={uw.scenarios.conventional.operating_expenses} negative />
                      <FinRow label="NOI" a={uw.scenarios.specialized.noi} b={uw.scenarios.conventional.noi} bold />
                      <FinRow label="Annual debt service" a={uw.scenarios.specialized.annual_debt_service} b={uw.scenarios.conventional.annual_debt_service} negative />
                      <FinRow label="Annual cash flow" a={uw.scenarios.specialized.annual_cash_flow} b={uw.scenarios.conventional.annual_cash_flow} bold accent />
                      <tr className="border-t border-slate-200 text-xs">
                        <td className="py-2 text-slate-500 uppercase tracking-wider">Metrics</td>
                        <td className="text-right py-2">{pct(uw.scenarios.specialized.cash_on_cash_pct)} CoC · {pct(uw.scenarios.specialized.cap_rate_pct)} cap · {uw.scenarios.specialized.dscr} DSCR</td>
                        <td className="text-right py-2">{pct(uw.scenarios.conventional.cash_on_cash_pct)} CoC · {pct(uw.scenarios.conventional.cap_rate_pct)} cap · {uw.scenarios.conventional.dscr} DSCR</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Card>

              {sens?.occupancy_curve?.length > 0 && (
                <Card className="md:col-span-3 p-5 border-slate-200 bg-white">
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Cash-flow sensitivity to occupancy (specialized)</div>
                  <div className="w-full h-64" style={{ minWidth: 0 }}>
                    <ResponsiveContainer width="100%" height="100%" minHeight={240}>
                      <LineChart data={sens.occupancy_curve} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="occupancy_pct" tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} label={{ value: "Occupancy", position: "insideBottom", offset: -2, style: { fontSize: 10, fill: "#64748B" } }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                        <Tooltip formatter={(v) => money(v)} labelFormatter={(l) => `Occupancy ${l}%`} />
                        <Line type="monotone" dataKey="cash_flow" stroke="#C25E00" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </Card>
              )}
            </div>
          )}
        </section>

        {/* Scenario Analysis */}
        {sens?.scenarios && (
          <section data-testid="report-scenarios">
            <H2>Scenario &amp; sensitivity analysis</H2>
            <div className="grid md:grid-cols-3 gap-4">
              {["conservative","base","upside"].map(k => {
                const r = sens.scenarios[k];
                if (!r) return null;
                const s = r.scenarios.specialized;
                const highlight = k === "base";
                return (
                  <Card key={k} className={`p-5 border-slate-200 ${highlight ? "bg-[#C25E00]/5 ring-1 ring-[#C25E00]" : "bg-white"}`}>
                    <div className={`text-[10px] uppercase tracking-widest font-semibold ${highlight ? "text-[#C25E00]" : "text-slate-500"}`}>{k}</div>
                    <div className="text-2xl font-bold font-mono mt-1">{money(s.annual_cash_flow)}<span className="text-xs text-slate-500 font-sans font-normal">/yr</span></div>
                    <div className="text-xs text-slate-500 mt-2 space-y-0.5">
                      <div>Vacancy: {r.inputs.spec_vacancy}%</div>
                      <div>OpEx: {r.inputs.spec_opex_pct}%</div>
                      <div>Rate: {r.inputs.interest_rate}%</div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-100 text-xs space-y-1">
                      <div className="flex justify-between"><span className="text-slate-500">NOI</span><span className="font-mono">{money(s.noi)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">DSCR</span><span className="font-mono">{s.dscr}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Cash-on-cash</span><span className="font-mono">{pct(s.cash_on_cash_pct)}</span></div>
                    </div>
                  </Card>
                );
              })}
            </div>
            <div className="text-xs text-slate-500 mt-2 italic">Assumptions are illustrative and rely on inputs captured on the property record. Modify inputs in the internal workspace to re-generate.</div>
          </section>
        )}

        {/* Opportunity & Confidence Analysis */}
        {score && (
          <section data-testid="report-scores">
            <H2>Opportunity &amp; confidence analysis</H2>
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="p-5 border-slate-200 bg-white">
                <div className="flex items-center justify-between">
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Opportunity Score</div>
                  <div className="text-3xl font-bold font-mono text-[#C25E00]">{score.opportunity_score}<span className="text-sm text-slate-400">/100</span></div>
                </div>
                <div className="mt-3 space-y-1.5">
                  {(score.opp_components || []).map((c, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className={c.kind === "penalty" || c.kind === "gate" ? "text-rose-600" : "text-slate-700"}>{c.label}</span>
                      <span className="font-mono font-semibold">{c.kind === "opp" ? "+" : ""}{c.value}</span>
                    </div>
                  ))}
                </div>
              </Card>
              <Card className="p-5 border-slate-200 bg-white">
                <div className="flex items-center justify-between">
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Confidence Score</div>
                  <div className="text-3xl font-bold font-mono text-slate-900">{score.confidence_score}<span className="text-sm text-slate-400">/100</span></div>
                </div>
                <div className="mt-3 space-y-1.5">
                  {(score.conf_components || []).map((c, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="text-slate-700">{c.label}</span>
                      <span className="font-mono font-semibold">+{c.value}</span>
                    </div>
                  ))}
                </div>
                {unresolved_items?.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-slate-100">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Actions to increase confidence</div>
                    <ul className="text-xs text-slate-700 space-y-0.5">
                      {unresolved_items.slice(0,4).map((u,i) => (<li key={i} className="flex gap-1"><ArrowRight className="w-3 h-3 mt-0.5 text-slate-400 shrink-0" /><span>{u}</span></li>))}
                    </ul>
                  </div>
                )}
              </Card>
            </div>
          </section>
        )}

        {/* Risks */}
        <section data-testid="report-risks">
          <H2>Risks &amp; mitigation</H2>
          <div className="grid md:grid-cols-2 gap-3">
            {risks.map((r, i) => (
              <Card key={i} className="p-4 border-slate-200 bg-white">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                  <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{r.category}</span>
                </div>
                <div className="text-sm text-slate-800 mt-2">{r.description}</div>
                {r.mitigation && (
                  <div className="mt-2 pt-2 border-t border-slate-100 text-xs text-emerald-800 flex gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                    <span>{r.mitigation}</span>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </section>

        {/* Roadmap */}
        <section data-testid="report-roadmap">
          <H2>Execution roadmap</H2>
          <Card className="p-5 border-slate-200 bg-white overflow-x-auto">
            <div className="flex items-stretch gap-1 min-w-[720px]">
              {roadmap_stages.map((s, i) => {
                const curIdx = roadmap_stages.indexOf(roadmap_current);
                const state = i < curIdx ? "done" : i === curIdx ? "current" : "future";
                const cls = state === "done" ? "bg-emerald-100 text-emerald-800 border-emerald-300" :
                            state === "current" ? "bg-[#C25E00] text-white border-[#C25E00]" :
                            "bg-slate-50 text-slate-500 border-slate-200";
                return (
                  <div key={s} className={`flex-1 min-w-[80px] px-3 py-3 border rounded-md text-center ${cls}`}>
                    <div className="text-[9px] uppercase tracking-widest font-bold opacity-70">Step {i+1}</div>
                    <div className="text-xs font-semibold mt-0.5 leading-tight">{ROADMAP_LABEL[s] || s}</div>
                  </div>
                );
              })}
            </div>
          </Card>
        </section>

        {/* Performance (operational stage only) */}
        {isOperational && (
          <section data-testid="report-performance">
            <H2>Performance</H2>
            <Card className="p-6 border-slate-200 bg-white">
              {Object.keys(performance || {}).length === 0 ? (
                <div className="text-sm text-slate-500 italic">Performance reporting has begun for this property. Detailed metrics will appear here as reporting periods are added.</div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Kv k="Occupancy" v={performance.occupancy_pct != null ? `${performance.occupancy_pct}%` : "—"} />
                  <Kv k="Placements" v={performance.placements ?? "—"} />
                  <Kv k="Actual revenue" v={performance.revenue != null ? money(performance.revenue) : "—"} />
                  <Kv k="Actual NOI" v={performance.noi != null ? money(performance.noi) : "—"} />
                </div>
              )}
              {(performance.updates || []).length > 0 && (
                <div className="mt-6 pt-6 border-t border-slate-100 space-y-2">
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Recent updates</div>
                  {(performance.updates || []).slice(0,6).map((u, i) => (
                    <div key={i} className="text-sm border-l-2 border-slate-200 pl-3 py-1">
                      <div className="text-xs text-slate-500">{u.date}</div>
                      <div>{u.body}</div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </section>
        )}

        {/* Exit & alternatives */}
        <section data-testid="report-exit">
          <H2>Exit &amp; alternative uses</H2>
          <Card className="p-5 border-slate-200 bg-white">
            <ul className="grid md:grid-cols-2 gap-2 text-sm text-slate-700">
              {exit_options.map((o, i) => (
                <li key={i} className="flex items-start gap-2"><TrendingUp className="w-3.5 h-3.5 text-slate-500 mt-1 shrink-0" /><span>{o}</span></li>
              ))}
            </ul>
          </Card>
        </section>

        {/* Sources */}
        <section data-testid="report-sources">
          <H2>Sources &amp; due diligence</H2>
          <Card className="p-5 border-slate-200 bg-white">
            {sources.length === 0 ? (
              <div className="text-sm text-slate-500 italic">No public sources cited yet.</div>
            ) : (
              <ul className="space-y-2 text-sm">
                {sources.map((s, i) => (
                  <li key={i}>
                    <a href={s.url} target="_blank" rel="noreferrer" className="text-[#C25E00] hover:underline inline-flex items-center gap-1">
                      {s.label} <ExternalLink className="w-3 h-3" />
                    </a>
                    {s.date && <span className="ml-2 text-xs text-slate-500">verified {s.date}</span>}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </section>

        {/* Compliance footer */}
        <div className="text-[11px] text-slate-500 pt-6 border-t border-slate-200 leading-relaxed" data-testid="report-compliance">
          {compliance}
          {share_expires_at && <> Shared link expires {new Date(share_expires_at).toLocaleDateString()}.</>}
        </div>
      </div>
    </div>
  );
}

/* ---------- Sub-components ---------- */
const H2 = ({ children }) => (
  <div className="mb-4">
    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">SECTION</div>
    <h2 className="text-2xl font-bold text-slate-900">{children}</h2>
  </div>
);

const Kv = ({ k, v, accent }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{k}</div>
    <div className={`text-sm mt-0.5 ${accent ? "text-[#C25E00] font-semibold" : "text-slate-800"}`}>{v ?? "—"}</div>
  </div>
);

const Info2 = ({ k, v }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{k}</div>
    <div className="text-sm text-slate-800 capitalize">{v ?? "—"}</div>
  </div>
);

const ScoreTile = ({ label, value, accent }) => (
  <div className="rounded-md border border-slate-200 bg-white p-3">
    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
    <div className="mt-1 flex items-baseline gap-1">
      <span className={`text-3xl font-bold font-mono ${accent ? "text-[#C25E00]" : "text-slate-900"}`}>{value ?? "—"}</span>
      <span className="text-xs text-slate-400 font-mono">/100</span>
    </div>
  </div>
);

const FinRow = ({ label, a, b, bold, accent, negative }) => (
  <tr className={bold ? "font-bold border-t border-slate-200" : ""}>
    <td className="py-1.5 text-slate-700 font-sans text-sm">{label}</td>
    <td className={`text-right py-1.5 ${accent ? "text-[#C25E00]" : negative ? "text-slate-600" : ""}`}>{money(a)}</td>
    <td className={`text-right py-1.5 ${negative ? "text-slate-600" : ""}`}>{money(b)}</td>
    <td className={`text-right py-1.5 text-xs ${(a - b) > 0 ? "text-emerald-700" : (a - b) < 0 ? "text-rose-600" : "text-slate-400"}`}>{a === b ? "—" : `${(a - b) > 0 ? "+" : ""}${money(a - b)}`}</td>
  </tr>
);

const EvidenceCol = ({ title, icon: Icon, tone, items, empty }) => {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50/60 text-emerald-800",
    amber: "border-amber-200 bg-amber-50/60 text-amber-800",
    rose: "border-rose-200 bg-rose-50/60 text-rose-800",
  };
  return (
    <Card className={`p-5 border ${tones[tone]}`}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" />
        <div className="text-xs uppercase tracking-widest font-semibold">{title}</div>
      </div>
      <ul className="mt-3 space-y-1.5 text-sm text-slate-800">
        {items.length === 0 ? <li className="text-xs italic opacity-70">{empty}</li> : items.map((x, i) => (
          <li key={i} className="flex gap-2"><span className="opacity-40">•</span><span>{x}</span></li>
        ))}
      </ul>
    </Card>
  );
};

const FlowDiagram = ({ funder, program, agency, operator, resident, propertyAddr, payment, verified }) => {
  const nodes = [
    { label: "Funding source", value: funder, tone: verified ? "verified" : "assumed" },
    { label: "Program / Agency", value: program, tone: "verified" },
    { label: "Operator / Referral", value: operator, tone: "assumed" },
    { label: "Resident placement", value: resident, tone: "assumed" },
    { label: "Property", value: propertyAddr, tone: "verified" },
    { label: "Payment", value: payment, tone: verified ? "verified" : "assumed" },
  ];
  return (
    <div className="overflow-x-auto">
      <div className="flex items-stretch gap-2 min-w-[820px]">
        {nodes.map((n, i) => (
          <div key={i} className="flex items-stretch flex-1">
            <div className={`flex-1 rounded-md p-3 border ${n.tone === "verified" ? "border-emerald-300 bg-emerald-50/50" : "border-amber-300 bg-amber-50/50"}`}>
              <div className="text-[9px] uppercase tracking-widest font-semibold text-slate-500">{n.label}</div>
              <div className="text-xs font-semibold text-slate-800 mt-1 leading-tight">{n.value || "—"}</div>
              <div className={`text-[9px] uppercase tracking-wider mt-2 font-bold ${n.tone === "verified" ? "text-emerald-700" : "text-amber-700"}`}>{n.tone === "verified" ? "Verified" : "Assumed"}</div>
            </div>
            {i < nodes.length - 1 && <div className="flex items-center px-1"><ArrowRight className="w-4 h-4 text-slate-400" /></div>}
          </div>
        ))}
      </div>
    </div>
  );
};

const referralVelocity = (wait) => {
  const w = (wait || "").toLowerCase();
  if (!w) return "—";
  if (w.includes("no wait") || w.includes("no open") || w.includes("same-day")) return "Fast";
  if (w.includes("10+") || w.includes("10 years") || w.includes("200,000")) return "Slow";
  return "Moderate";
};
